import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, Edit, CheckCircle, X, Trash2, Loader, User, Mail, BookOpen, Phone, Globe, Calendar, Clock, Download, UserCheck, FileText, MapPin, GraduationCap, School } from 'lucide-react';
import { toast } from 'react-hot-toast';
import PropTypes from 'prop-types';
import * as XLSX from 'xlsx';
import apiService from '../../services/api';
import { useApiData } from '../../hooks/useApi';
import Modal from '../common/Modal';

const Admissions = ({ user, partnerFilter, onClearPartnerFilter }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [deletingAdmission, setDeletingAdmission] = useState(null);
  const [editData, setEditData] = useState({});
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [statusData, setStatusData] = useState({ status: '', remarks: '' });
  const [isAllocating, setIsAllocating] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);



  const { data: admissionsData, loading, refetch } = useApiData(
    () => {
      const params = { search: searchTerm, status: statusFilter };
      if (partnerFilter?.id) {
        params.referral_partner_id = partnerFilter.id;
      }
      return apiService.getAdmissions(params);
    },
    [searchTerm, statusFilter, partnerFilter]
  );

  const { data: partnersData, loading: partnersLoading } = useApiData(
    () => apiService.getTeamLeads(),
    []
  );

  const admissions = Array.isArray(admissionsData?.data) ? admissionsData.data :
    Array.isArray(admissionsData) ? admissionsData : [];
  const partners = Array.isArray(partnersData?.data) ? partnersData.data :
    Array.isArray(partnersData) ? partnersData : [];

  const handleView = (admission) => {
    setSelectedAdmission(admission);
    setShowViewModal(true);
  };

  const handleEdit = (admission) => {
    setSelectedAdmission(admission);
    setEditData(admission);
    setShowEditModal(true);
  };

  const handleStatusUpdate = async (admission, newStatus) => {
    try {
      await apiService.put(`/admissions/${admission.id}`, { status: newStatus });
      toast.success(`Admission ${newStatus} successfully`);
      refetch();
    } catch (error) {
      toast.error('Failed to update admission status');
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      await apiService.put(`/admissions/${selectedAdmission.id}`, editData);
      toast.success('Admission updated successfully');
      setShowEditModal(false);
      refetch();
    } catch (error) {
      toast.error('Failed to update admission');
    }
  };

  const handleDelete = (admission) => {
    setDeletingAdmission(admission);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await apiService.deleteAdmission(deletingAdmission.id);
      toast.success('Admission deleted successfully');
      refetch();
    } catch (error) {
      toast.error(error.message || 'Failed to delete admission');
    } finally {
      setShowDeleteModal(false);
      setDeletingAdmission(null);
    }
  };

  const handleDownload = (admission) => {
    const workbook = XLSX.utils.book_new();
    const worksheetData = [
      ['Field', 'Value'],
      ['Name', admission.name || ''],
      ['Location', admission.location || ''],
      ['Class', admission.class || ''],
      ['School', admission.school || ''],
      ['Mobile', admission.mobile || ''],
      ['WhatsApp', admission.whatsapp || ''],
      ['Email', admission.email || ''],
      ['Preferred Country', admission.preferred_country || ''],
      ['Preferred University', admission.preferred_university || ''],
      ['Referral Partner ID', admission.referral_partner_id || ''],
      ['Status', admission.status || ''],
      ['Date of Submission', new Date(admission.created_at || admission.updated_at).toLocaleDateString('en-GB')]
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Admission Details');

    const fileName = `admission_${admission.name?.replace(/\s+/g, '_') || 'record'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    toast.success('Excel file downloaded successfully');
  };

  const handleAllocateTo = (admission) => {
    setSelectedAdmission(admission);
    setSelectedPartnerId(admission.referral_partner_id || '');
    setShowAllocationModal(true);
  };

  const handleAllocationSave = async () => {
    if (!selectedPartnerId) {
      toast.error('Please select a referral partner');
      return;
    }

    setIsAllocating(true);
    try {
      await apiService.allocateAdmission(selectedAdmission.id, selectedPartnerId);
      toast.success('Admission allocated successfully');
      setShowAllocationModal(false);
      refetch();
    } catch (error) {
      toast.error('Failed to allocate admission');
    } finally {
      setIsAllocating(false);
    }
  };

  const handleApplicationStatus = (admission) => {
    setSelectedAdmission(admission);
    setStatusData({ status: admission.status || '', remarks: '' });
    setShowStatusModal(true);
  };

  const handleStatusSave = async () => {
    if (!statusData.status) {
      toast.error('Please select a status');
      return;
    }

    setIsUpdatingStatus(true);
    try {
      await apiService.updateApplicationStatus(selectedAdmission.id, statusData.status, statusData.remarks);
      toast.success('Application status updated successfully');
      setShowStatusModal(false);
      refetch();
    } catch (error) {
      toast.error('Failed to update application status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="responsive-card">
        <div className="responsive-loading">
          <div className="responsive-spinner" />
          <p>Loading admissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-section">
      <div className="section-header">
        <h2>Admissions Enquiry Management</h2>
        {partnerFilter && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: 'var(--primary)' }}>
            <span>Filtered by: {partnerFilter.name}</span>
            <button
              onClick={onClearPartnerFilter}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--danger)',
                cursor: 'pointer',
                padding: '0.25rem'
              }}
              title="Clear filter"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      <div className="filters">
        <input
          className="search-input"
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search enquiries..."
          style={{ flex: 1 }}
          type="text"
          value={searchTerm}
        />
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Allocated">Allocated</option>
          <option value="Processing">Processing</option>
          <option value="Registered">Registered</option>
          <option value="Processing fee Part 1 Paid">Processing fee Part 1 Paid</option>
          <option value="Processing fee Part 2 Paid">Processing fee Part 2 Paid</option>
          <option value="Cancelled Registration">Cancelled Registration</option>
          <option value="Not Interested">Not Interested</option>
        </select>
      </div>

      <div className="table-container table-responsive">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name of Applicant</th>
              <th>Date of Submission</th>
              <th>Preferred Country</th>
              <th>Preferred University</th>
              {user.role !== 'team_lead' && <th>Allocated To</th>}
              <th>Status</th>
              {user.role !== 'referral_partner' && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {admissions.map((admission, index) => (
              <motion.tr
                key={admission.id}
                animate={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
              >
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div className="user-avatar" style={{ width: '32px', height: '32px', fontSize: '0.75rem' }}>
                      {admission.name?.split(' ').filter(n => n).map(n => n[0]).join('') || '?'}
                    </div>
                    <span>{admission.name}</span>
                  </div>
                </td>
                <td>
                  {new Date(admission.created_at || admission.updated_at).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric"
                  })}
                </td>
                <td>{admission.preferred_country || admission.country || 'Not specified'}</td>
                <td>{admission.preferred_university || admission.university || 'Not specified'}</td>
                {user.role !== 'team_lead' && (
                  <td>
                    {admission.AllocatedPartner?.role === 'team_lead'
                      ? admission.AllocatedPartner?.name
                      : 'Not Allocated'}
                  </td>
                )}
                <td><span className={`status-badge ${admission.status?.toLowerCase().replace(/\s+/g, '-')}`}>
                  {admission.status}
                </span></td>

                <td>
                  {user.role === 'referral_partner' ? (
                    <div></div>
                  ) : user.role === 'team_lead' ? (
                    <div className="action-buttons">
                      <button
                        className="btn-sm primary touch-target"
                        onClick={() => handleView(admission)}
                        title="View Application"
                      >
                        <Eye size={12} />
                        <span>VIEW APPLICATION</span>
                      </button>
                      <button
                        className="btn-sm warning touch-target"
                        onClick={() => handleApplicationStatus(admission)}
                        title="Application Status"
                      >
                        <FileText size={12} />
                        <span>APPLICATION STATUS</span>
                      </button>
                    </div>
                  ) : (
                    <div className="action-buttons">
                      <button
                        className="btn-sm primary touch-target"
                        onClick={() => handleDownload(admission)}
                        title="Download"
                      >
                        <Download size={12} />
                        <span>DOWNLOAD</span>
                      </button>
                      <button
                        className="btn-sm success touch-target"
                        onClick={() => handleAllocateTo(admission)}
                        title="Allocated To"
                      >
                        <UserCheck size={12} />
                        <span>ALLOCATED TO</span>
                      </button>
                      <button
                        className="btn-sm warning touch-target"
                        onClick={() => handleApplicationStatus(admission)}
                        title="Application Status"
                      >
                        <FileText size={12} />
                        <span>APPLICATION STATUS</span>
                      </button>
                      {user.role === 'super_admin' && (
                        <button
                          className="btn-sm danger touch-target"
                          onClick={() => handleDelete(admission)}
                          title="Delete"
                        >
                          <Trash2 size={12} />
                          <span>DELETE</span>
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Admission Details"
      >
        {selectedAdmission && (
          <div className="admission-details-enhanced">
            {/* Header Section with Avatar and Status */}
            <div className="details-header">
              <div className="student-info">
                <div className="student-avatar">
                  {selectedAdmission.name?.split(' ').filter(n => n).map(n => n[0]).join('') || '?'}
                </div>
                <div className="student-meta">
                  <h3>{selectedAdmission.name}</h3>
                  <span className={`status-badge ${selectedAdmission.status?.toLowerCase().replace(/\s+/g, '-')}`}>
                    {selectedAdmission.status}
                  </span>
                </div>
              </div>
              <div className="application-date">
                <Calendar size={16} />
                <span>Applied on {new Date(selectedAdmission.created_at).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric"
                })}</span>
              </div>
            </div>

            {/* Details Grid */}
            <div className="details-grid">
              <div className="detail-card">
                <div className="detail-icon">
                  <Mail size={18} />
                </div>
                <div className="detail-content">
                  <label>Email</label>
                  <p>{selectedAdmission.email || 'Not provided'}</p>
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-icon">
                  <Phone size={18} />
                </div>
                <div className="detail-content">
                  <label>Mobile</label>
                  <p>{selectedAdmission.mobile || 'Not provided'}</p>
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-icon">
                  <Phone size={18} />
                </div>
                <div className="detail-content">
                  <label>WhatsApp</label>
                  <p>{selectedAdmission.whatsapp || 'Not provided'}</p>
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-icon">
                  <MapPin size={18} />
                </div>
                <div className="detail-content">
                  <label>Location</label>
                  <p>{selectedAdmission.location || 'Not provided'}</p>
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-icon">
                  <GraduationCap size={18} />
                </div>
                <div className="detail-content">
                  <label>Class</label>
                  <p>{selectedAdmission.class || 'Not provided'}</p>
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-icon">
                  <School size={18} />
                </div>
                <div className="detail-content">
                  <label>School</label>
                  <p>{selectedAdmission.school || 'Not provided'}</p>
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-icon">
                  <Globe size={18} />
                </div>
                <div className="detail-content">
                  <label>Preferred Country</label>
                  <p>{selectedAdmission.preferred_country || 'Not provided'}</p>
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-icon">
                  <BookOpen size={18} />
                </div>
                <div className="detail-content">
                  <label>Preferred University</label>
                  <p>{selectedAdmission.preferred_university || 'Not provided'}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Admission"
      >
        {selectedAdmission && (
          <form onSubmit={handleSaveEdit} className="modal-form">
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={editData.name || ''}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={editData.email || ''}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Course *</label>
                <input
                  type="text"
                  value={editData.course || ''}
                  onChange={(e) => setEditData({ ...editData, course: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={editData.phone || ''}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  value={editData.country || ''}
                  onChange={(e) => setEditData({ ...editData, country: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={editData.status || 'Pending'}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                >
                  <option value="Pending">Pending</option>
                  <option value="Allocated">Allocated</option>
                  <option value="Processing">Processing</option>
                  <option value="Registered">Registered</option>
                  <option value="Processing fee Part 1 Paid">Processing fee Part 1 Paid</option>
                  <option value="Processing fee Part 2 Paid">Processing fee Part 2 Paid</option>
                  <option value="Cancelled Registration">Cancelled Registration</option>
                  <option value="Not Interested">Not Interested</option>
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">
                Update Admission
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingAdmission(null);
        }}
        title="Confirm Delete"
      >
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <p>Are you sure you want to delete this admission enquiry?</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
            <button
              className="btn-sm danger"
              onClick={confirmDelete}
            >
              Delete
            </button>
            <button
              className="btn-sm"
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingAdmission(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* Allocation Modal */}
      <Modal
        isOpen={showAllocationModal}
        onClose={() => setShowAllocationModal(false)}
        title="Allocate to Academic Counsellor"
      >
        <div className="modal-form">
          <div className="form-group">
            <label>Select a Academic Counsellor</label>
            {partnersLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Loader size={16} className="spin" />
                <span>Loading Academic Counsellor...</span>
              </div>
            ) : (
              <select
                value={selectedPartnerId}
                onChange={(e) => setSelectedPartnerId(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="">Select a Academic Counsellor</option>
                {partnersData?.data?.map((partner) => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name} ({partner.email})
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="form-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button
              className="btn-sm"
              onClick={() => setShowAllocationModal(false)}
            >
              Cancel
            </button>
            <button
              className="btn-sm success"
              onClick={handleAllocationSave}
              disabled={!selectedPartnerId || partnersLoading || isAllocating}
            >
              {isAllocating ? (
                <>
                  <Loader size={12} className="spin" />
                  Allocating...
                </>
              ) : (
                'Allocate'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Application Status Modal */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Update Application Status"
      >
        <div className="modal-form">
          <div className="form-group">
            <label>Application Status</label>
            <select
              value={statusData.status}
              onChange={(e) => setStatusData({ ...statusData, status: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">Select status</option>
              <option value="Pending">Pending</option>
              <option value="Allocated">Allocated</option>
              <option value="Processing">Processing</option>
              <option value="Registered">Registered</option>
              <option value="Processing fee Part 1 Paid">Processing fee Part 1 Paid</option>
              <option value="Processing fee Part 2 Paid">Processing fee Part 2 Paid</option>
              <option value="Cancelled Registration">Cancelled Registration</option>
              <option value="Not Interested">Not Interested</option>
            </select>
          </div>
          {/* <div className="form-group">
            <label>Remarks (Optional)</label>
            <textarea
              value={statusData.remarks}
              onChange={(e) => setStatusData({ ...statusData, remarks: e.target.value })}
              placeholder="Add any remarks or notes..."
              rows={3}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', resize: 'vertical' }}
            />
          </div> */}
          <div className="form-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button
              className="btn-sm"
              onClick={() => setShowStatusModal(false)}
            >
              Cancel
            </button>
            <button
              className="btn-sm primary"
              onClick={handleStatusSave}
              disabled={!statusData.status || isUpdatingStatus}
            >
              {isUpdatingStatus ? (
                <>
                  <Loader size={12} className="spin" />
                  Updating...
                </>
              ) : (
                'Update Status'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

Admissions.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string,
    role: PropTypes.string,
  }).isRequired,
  partnerFilter: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  onClearPartnerFilter: PropTypes.func,
};

export default Admissions;
